/**
 * Complete the GitLab OAuth callback (Story 013.2 AC3, AC4, AC6).
 *
 * `parseCallbackQuery` reads ONLY `location.search` — with
 * `createWebHashHistory`, GitLab's real query string never lives in the
 * hash fragment, so the hash must not be inspected (AC4).
 *
 * `completeCallback` always returns a static, masked failure message on any
 * error path rather than forwarding the underlying error's text — the
 * exchange can fail with an upstream message that echoes the raw code or
 * token, and no per-pattern regex could safely cover every provider-error
 * shape, so nothing dynamic from the failure is ever surfaced (AC6).
 *
 * A code-less, error-less query (a cold hit on `/callback`) is tagged with
 * `reason: 'missing_code'` so the caller can redirect to `/login` quietly
 * instead of showing an alert. A GitLab-reported `error` (e.g. the user
 * denied access) or a real exchange failure are genuine outcomes the user
 * should see, so neither carries this reason.
 */
import { exchangeCode } from '@gl-demo-ultimate-odupre/connect-with-gitlab';
import type { ResolvedOAuth2Auth, TokenSet } from '@gl-demo-ultimate-odupre/connect-with-gitlab';
import { OAUTH_STATE_SESSION_KEY, OAUTH_CODE_VERIFIER_SESSION_KEY } from './oauthLogin';

export interface OAuthCallbackQuery {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
}

export function parseCallbackQuery(location: Pick<Location, 'search'>): OAuthCallbackQuery {
  const params = new URLSearchParams(location.search);
  const query: OAuthCallbackQuery = {};
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  const errorDescription = params.get('error_description');
  if (code !== null) query.code = code;
  if (state !== null) query.state = state;
  if (error !== null) query.error = error;
  if (errorDescription !== null) query.error_description = errorDescription;
  return query;
}

export interface CompleteCallbackDeps {
  sessionStorage: Storage;
}

export type CompleteCallbackResult =
  | { ok: true; tokenSet: TokenSet }
  | { ok: false; message: string; reason?: 'missing_code' };

const GENERIC_FAILURE_MESSAGE = 'Sign-in failed. Please try again.';

export async function completeCallback(
  query: OAuthCallbackQuery,
  auth: ResolvedOAuth2Auth,
  deps: CompleteCallbackDeps,
): Promise<CompleteCallbackResult> {
  const { sessionStorage } = deps;
  try {
    if (query.error) {
      return { ok: false, message: GENERIC_FAILURE_MESSAGE };
    }
    if (!query.code) {
      return { ok: false, message: GENERIC_FAILURE_MESSAGE, reason: 'missing_code' };
    }
    const codeVerifier = sessionStorage.getItem(OAUTH_CODE_VERIFIER_SESSION_KEY) ?? '';
    const expectedState = sessionStorage.getItem(OAUTH_STATE_SESSION_KEY) ?? '';
    const tokenSet = await exchangeCode({
      auth,
      code: query.code,
      codeVerifier,
      state: query.state ?? '',
      expectedState,
    });
    return { ok: true, tokenSet };
  } catch {
    return { ok: false, message: GENERIC_FAILURE_MESSAGE };
  } finally {
    sessionStorage.removeItem(OAUTH_STATE_SESSION_KEY);
    sessionStorage.removeItem(OAUTH_CODE_VERIFIER_SESSION_KEY);
  }
}
